import { AlertTriangle, BarChart3, DatabaseZap } from "lucide-react";
import { cn } from "../../ui/utils";

function Shimmer({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <span
      className={cn("relative block overflow-hidden rounded-md", className)}
      style={{ background: "var(--ca-skel-base)", ...style }}
    >
      <span
        aria-hidden
        className="absolute inset-0 animate-ca-shimmer"
        style={{ background: "linear-gradient(90deg, transparent, var(--ca-skel-hi), transparent)" }}
      />
    </span>
  );
}

const SKELETON_BARS = [46, 72, 58, 88, 64, 40, 76];

export function ChartSkeleton({ height = 208 }: { height?: number }) {
  return (
    <div className="w-full" style={{ height }} aria-hidden>
      <div className="flex h-full items-end gap-2 px-1 pb-5">
        {SKELETON_BARS.map((h, i) => (
          <Shimmer key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%` }} />
        ))}
      </div>
      <Shimmer className="h-2 w-1/3" />
    </div>
  );
}

function StatusBody({
  icon,
  title,
  message,
  tone = "muted",
}: {
  icon: React.ReactNode;
  title: string;
  message?: string;
  tone?: "muted" | "error";
}) {
  return (
    <div
      role="status"
      className="flex min-h-[9rem] flex-col items-center justify-center gap-2 px-4 py-6 text-center"
    >
      <div
        className={cn(
          "flex size-10 items-center justify-center rounded-full",
          tone === "error"
            ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300"
            : "bg-muted-foreground/10 text-muted-foreground",
        )}
      >
        {icon}
      </div>
      <p className="text-xs font-semibold text-foreground">{title}</p>
      {message && (
        <p className="max-w-[16rem] text-[0.6875rem] leading-snug text-muted-foreground">{message}</p>
      )}
    </div>
  );
}

export function ChartEmpty({ message }: { message?: string }) {
  return (
    <StatusBody
      icon={<BarChart3 className="size-5" />}
      title="Nothing to chart yet"
      message={message ?? "There is no data in this range to visualize."}
    />
  );
}

export function ChartNoData({ message }: { message?: string }) {
  return (
    <StatusBody
      icon={<DatabaseZap className="size-5" />}
      title="No data available"
      message={message ?? "This chart could not be built because no data was returned."}
    />
  );
}

export function ChartError({ message }: { message?: string }) {
  return (
    <StatusBody
      tone="error"
      icon={<AlertTriangle className="size-5" />}
      title="Couldn't load this chart"
      message={message ?? "Something went wrong while preparing this visualization."}
    />
  );
}
